import { Component, OnDestroy, OnInit } from '@angular/core';

import { ButtonModule } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TwoFactorAuthenticationService } from '../../service/two-factor-authentication.service';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { QRCodeComponent } from 'angularx-qrcode';
import { ClipboardService } from '@shared/services/clipboard/clipboard.service';
import { NotificationService } from '@shared/services/notifications/notification.service';
import { finalize, first } from 'rxjs';
import { TotpSecret } from 'firebase/auth';
import { AppFormFieldErrorDirective } from '@shared/directives/app-form-field-error.directive';
import { LoadingStateHandlerService } from '@shared/services/loading-state-handler/loading-state-handler.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SkeletonModule } from 'primeng/skeleton';
import { InputOtpModule } from 'primeng/inputotp';
import { getFirebaseErrorMessage } from '@core/services/auth-firebase/firebase-error-map';

@Component({
  selector: 'app-qr-enable',
  standalone: true,
  imports: [
    ButtonModule,
    ReactiveFormsModule,
    QRCodeComponent,
    AppFormFieldErrorDirective,
    SkeletonModule,
    InputOtpModule
],
  templateUrl: './qr-enable.component.html',
  styleUrl: './qr-enable.component.scss',
})
export class QrEnableComponent implements OnInit, OnDestroy {
  form: FormGroup;
  submitting: boolean = false;
  totpUri: string = '';
  totpSecret: TotpSecret | null = null;
  secretKey: string = '';
  loadingComponent: boolean = false;

  readonly CODE_VALIDITY_MS: number = 2 * 60 * 1000; // 2 minutos
  readonly RESEND_COOLDOWN_MS: number = 25 * 1000; // 25 segundos

  codeExpiryAt: number | null = null;
  remainingMs: number = 0;
  validityInterval: any = null;
  isExpired: boolean = false;

  canResend: boolean = true;
  resendCooldownRemainingMs: number = 0;
  resendInterval: any = null;

  constructor(
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    private twoFactorAuthenticationService: TwoFactorAuthenticationService,
    private notificationService: NotificationService,
    private clipboardService: ClipboardService,
    private loadingStateHandlerService: LoadingStateHandlerService
  ) {
    this.setLoadingHandler();
    this.form = this.fb.group({
      code: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });
  }

  ngOnInit() {
    this.loadQrCode();
  }

  ngOnDestroy(): void {
    this.clearValidityTimer();
    this.clearResendCooldown();
  }

  loadQrCode(): void {
    this.loadingComponent = true;
    this.clearValidityTimer();
    this.twoFactorAuthenticationService
      .generateTotpSecret()
      .pipe(
        first(),
        finalize(() =>
          this.loadingStateHandlerService.setDataLoaded(
            this.constructor.name,
            'qrCode'
          )
        )
      )
      .subscribe({
        next: ({ totpUri, totpSecret, secretKey }) => {
          this.totpUri = totpUri;
          this.totpSecret = totpSecret;
          this.secretKey = secretKey;
          this.isExpired = false;
          this.codeExpiryAt = Date.now() + this.CODE_VALIDITY_MS;
          this.startValidityTimer();
          this.startResendCooldown();
        },
        error: () => {
          this.notificationService.show(
            'Error',
            'Error cargando el código QR',
            'error'
          );
        },
      });
  }

  private setLoadingHandler() {
    this.loadingStateHandlerService.loadDataState(this.constructor.name, {
      qrCode: false,
    });

    this.loadingStateHandlerService
      .getLoadingState(this.constructor.name)
      .pipe(takeUntilDestroyed())
      .subscribe((loading) => (this.loadingComponent = loading));
  }

  onSubmit() {
    if (this.form.invalid || !this.totpSecret) return;
    if (this.isExpired) {
      this.notificationService.show(
        'Error',
        'El código ha vencido. Genera un nuevo QR.',
        'error'
      );
      return;
    }
    this.submitting = true;
    this.twoFactorAuthenticationService
      .saveTotp(this.totpSecret, this.form.value.code)
      .pipe(
        first(),
        finalize(() => (this.submitting = false))
      )
      .subscribe({
        next: () => {
          this.ref.close(true);
          this.notificationService.show(
            'Info',
            'Proceso de autenticación activado'
          );
        },
        error: (error) => {
          this.notificationService.show(
            'Error',
            'Error al activar la autenticación de dos factores - detalles: ' +
              getFirebaseErrorMessage(error.code),
            'error'
          );
        },
      });
  }

  copyToClipboard(): void {
    this.clipboardService.copy(
      this.secretKey,
      'twoFactorAuthentication.copySecretSuccess'
    );
  }

  private startValidityTimer(): void {
    this.remainingMs = Math.max(0, (this.codeExpiryAt || 0) - Date.now());
    this.clearValidityTimer();
    this.validityInterval = setInterval(() => {
      this.remainingMs = Math.max(0, (this.codeExpiryAt || 0) - Date.now());
      if (this.remainingMs <= 0) {
        this.isExpired = true;
        this.totpSecret = null;
        this.clearValidityTimer();
      }
    }, 1000);
  }

  private clearValidityTimer(): void {
    if (this.validityInterval) {
      clearInterval(this.validityInterval);
      this.validityInterval = null;
    }
  }

  private startResendCooldown(): void {
    this.canResend = false;
    this.resendCooldownRemainingMs = this.RESEND_COOLDOWN_MS;
    this.clearResendCooldown();
    this.resendInterval = setInterval(() => {
      this.resendCooldownRemainingMs = Math.max(
        0,
        this.resendCooldownRemainingMs - 1000
      );
      if (this.resendCooldownRemainingMs <= 0) {
        this.canResend = true;
        this.clearResendCooldown();
      }
    }, 1000);
  }

  private clearResendCooldown(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
  }

  regenerate(): void {
    if (!this.canResend) return;
    this.loadQrCode();
  }

  get remainingLabel(): string {
    const totalSeconds: number = Math.ceil(this.remainingMs / 1000);
    const minutes: string = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds: string = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  get resendCooldownSeconds(): number {
    return Math.ceil(this.resendCooldownRemainingMs / 1000);
  }
}
